import { useEffect, useState, useRef } from "react";
import { ArrowLeft, Send, Paperclip, HelpCircle, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";

// Sample conversation data for help needed channel
const conversationData = [
  {
    id: 1,
    sender: "ContentBot",
    avatarSrc: "/crew-agents/content-generator.jpg",
    message: "I need human assistance with a content strategy decision for a new market segment we're analyzing.",
    timestamp: "Today at 9:15 AM",
    isUser: false,
    priority: "medium"
  },
  {
    id: 2,
    sender: "VoiceBot",
    avatarSrc: "/crew-agents/voicebot.jpg",
    message: "I've detected a pattern in user voice interactions that I can't properly interpret. Could use human expertise to clarify intent patterns.",
    timestamp: "Today at 10:20 AM",
    isUser: false,
    priority: "low"
  },
  {
    id: 3,
    sender: "You",
    avatarSrc: "/crew-agents/page-semantic.png",
    message: "ContentBot, what specifically are you struggling with in the content strategy?",
    timestamp: "Today at 10:35 AM",
    isUser: true
  },
  {
    id: 4,
    sender: "ContentBot",
    avatarSrc: "/crew-agents/content-generator.jpg",
    message: "Thanks for the quick response! I'm analyzing content engagement for the healthcare sector, and there's a conflict between what our data shows (technical content performs best) and industry benchmarks (simplified content performs best). I need guidance on which direction to prioritize.",
    timestamp: "Today at 10:38 AM",
    isUser: false
  },
  {
    id: 5,
    sender: "You",
    avatarSrc: "/crew-agents/page-semantic.png",
    message: "For healthcare, we should trust our own data first. Can you segment it further by user role (practitioner vs. admin) to see if that explains the discrepancy?",
    timestamp: "Today at 10:42 AM",
    isUser: true
  },
  {
    id: 6,
    sender: "ContentBot",
    avatarSrc: "/crew-agents/content-generator.jpg",
    message: "That's an excellent suggestion. I'll segment the data and analyze engagement patterns by role. I'll have results ready in about 2 hours.",
    timestamp: "Today at 10:45 AM",
    isUser: false
  },
  {
    id: 7,
    sender: "BugHunter",
    avatarSrc: "/crew-agents/intent-checker.png",
    message: "URGENT: I've detected a critical performance issue with the analytics dashboard when processing datasets larger than 50,000 entries. System memory usage spikes to 95% and response times exceed 10 seconds. This is affecting approximately 15% of enterprise customers right now.",
    timestamp: "Today at 11:30 AM",
    isUser: false,
    priority: "high"
  },
  {
    id: 8,
    sender: "You",
    avatarSrc: "/crew-agents/page-semantic.png",
    message: "This is serious. Have you identified the specific component causing the memory leak?",
    timestamp: "Today at 11:32 AM",
    isUser: true
  },
  {
    id: 9,
    sender: "BugHunter",
    avatarSrc: "/crew-agents/intent-checker.png",
    message: "Yes, I've isolated it to the real-time visualization component. When the dataset size exceeds 50K entries, the data processing function is not properly batch-processing the inputs. Need a human developer to review the code in src/components/visualization/RealTimeChart.js - specifically the useEffect hook that's handling data updates.",
    timestamp: "Today at 11:35 AM",
    isUser: false
  }
];

// Team members for this channel
const teamMembers = [
  {
    name: "ContentBot",
    role: "Content Analyzer",
    avatarSrc: "/crew-agents/content-generator.jpg",
    status: "online"
  },
  {
    name: "VoiceBot",
    role: "Voice Assistant",
    avatarSrc: "/crew-agents/voicebot.jpg", 
    status: "away"
  },
  {
    name: "BugHunter",
    role: "QA Specialist",
    avatarSrc: "/crew-agents/intent-checker.png",
    status: "online"
  },
  {
    name: "You",
    role: "Product Manager",
    avatarSrc: "/crew-agents/page-semantic.png",
    status: "online"
  }
];

// Help request items
const helpRequests = [
  {
    id: 101,
    agent: "BugHunter",
    avatarSrc: "/crew-agents/intent-checker.png",
    title: "Critical Performance Issue",
    description: "Analytics dashboard performance degradation with large datasets",
    priority: "high",
    timeAgo: "35 minutes ago"
  },
  {
    id: 102,
    agent: "ContentBot",
    avatarSrc: "/crew-agents/content-generator.jpg",
    title: "Content Strategy Decision",
    description: "Need guidance on healthcare content priority (technical vs. simplified)",
    priority: "medium",
    timeAgo: "3 hours ago",
    inProgress: true
  },
  {
    id: 103,
    agent: "VoiceBot",
    avatarSrc: "/crew-agents/voicebot.jpg",
    title: "Voice Intent Pattern Analysis",
    description: "Unusual patterns in user voice interactions need human interpretation",
    priority: "low",
    timeAgo: "1 hour ago"
  }
];

const ChatMessage = ({ message }: { message: any }) => {
  const isUser = message.isUser;
  
  return (
    <div className={`flex gap-3 mb-4 ${isUser ? 'justify-end' : ''}`}>
      {!isUser && (
        <div className="flex-shrink-0">
          <div className="relative">
            <img 
              src={message.avatarSrc} 
              alt={message.sender}
              className="h-8 w-8 rounded-full object-cover"
            />
            {message.priority && (
              <div className={`absolute -top-1 -right-1 h-3 w-3 rounded-full border-2 border-background ${
                message.priority === 'high' ? 'bg-red-500' : 
                message.priority === 'medium' ? 'bg-yellow-500' : 'bg-blue-500'
              }`} />
            )}
          </div>
        </div>
      )}
      
      <div className={`max-w-[70%] ${isUser ? 'bg-primary text-primary-foreground' : 'bg-muted'} rounded-lg px-4 py-2`}>
        <div className="flex justify-between items-baseline mb-1">
          <span className={`font-medium text-sm ${isUser ? 'text-primary-foreground' : 'text-foreground'}`}>
            {message.sender}
          </span>
          <span className={`text-xs ml-4 ${isUser ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
            {message.timestamp}
          </span>
        </div>
        <p className={`text-sm whitespace-pre-line ${isUser ? 'text-primary-foreground' : 'text-foreground'}`}>
          {message.message}
        </p>
      </div>
      
      {isUser && (
        <img 
          src={message.avatarSrc} 
          alt={message.sender}
          className="h-8 w-8 rounded-full object-cover mt-1"
        />
      )}
    </div>
  );
};

const HelpRequestCard = ({ request }: { request: any }) => {
  return (
    <Card className={`mb-3 ${
      request.priority === 'high' ? 'border-red-200 bg-red-50' : 
      request.priority === 'medium' ? 'border-yellow-200 bg-yellow-50' : 
      'border-blue-200 bg-blue-50'
    }`}>
      <CardHeader className="py-2 px-3">
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-2">
            <img 
              src={request.avatarSrc} 
              alt={request.agent}
              className="h-6 w-6 rounded-full object-cover"
            />
            <CardTitle className="text-sm font-medium">{request.agent}</CardTitle>
          </div>
          <Badge className={`
            ${request.priority === 'high' ? 'bg-red-500' : 
              request.priority === 'medium' ? 'bg-yellow-500' : 'bg-blue-500'}
          `}>
            {request.priority.charAt(0).toUpperCase() + request.priority.slice(1)}
          </Badge>
        </div>
        <CardDescription className="mt-1 text-xs">
          Requested {request.timeAgo}
        </CardDescription>
      </CardHeader>
      <CardContent className="py-2 px-3">
        <h4 className="font-medium text-sm mb-1">{request.title}</h4>
        <p className="text-xs text-muted-foreground">{request.description}</p>
      </CardContent>
      <CardFooter className="py-2 px-3 flex justify-between">
        {request.inProgress ? (
          <Badge variant="outline" className="bg-green-100 text-green-800 text-xs">In Progress</Badge>
        ) : (
          <span></span>
        )}
        <Button variant="ghost" size="sm" className="h-7 text-xs">
          View Details
        </Button>
      </CardFooter>
    </Card>
  );
};

const HelpNeededChannelPage = () => {
  const navigate = useNavigate();
  const [message, setMessage] = useState("");
  const [conversation, setConversation] = useState(conversationData);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    document.title = "Help Needed Channel | PageMind Admin";
    
    // Scroll to bottom of messages
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [conversation]);

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (message.trim()) {
      // Add user message
      const newUserMessage = {
        id: conversation.length + 1,
        sender: "You",
        avatarSrc: "/crew-agents/page-semantic.png",
        message,
        timestamp: "Just now",
        isUser: true
      };
      
      setConversation([...conversation, newUserMessage]);
      setMessage("");
      
      // Simulate response after a delay
      setTimeout(() => {
        const botResponse = {
          id: conversation.length + 2,
          sender: "BugHunter",
          avatarSrc: "/crew-agents/intent-checker.png",
          message: "I'll escalate this to the development team right away. Would you like me to set up an emergency meeting with the backend engineers?",
          timestamp: "Just now",
          isUser: false
        };
        
        setConversation(prev => [...prev, botResponse]);
      }, 1000);
    }
  };

  return (
    <div className="container mx-auto p-6 h-[calc(100vh-4rem)] flex flex-col">
      {/* Header */}
      <div className="flex justify-between items-center mb-4 pb-4 border-b">
        <div className="flex items-center">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate('/insights/channels')}
            className="mr-2"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          
          <div className="flex items-center">
            <div className="flex items-center gap-1 bg-primary/10 rounded-full px-3 py-1.5">
              <HelpCircle className="h-5 w-5 text-primary" />
              <div>
                <div className="font-bold flex items-center gap-2">
                  Help Needed
                  <Badge variant="outline" className="text-xs bg-primary/5">Team Channel</Badge>
                  <Badge className="bg-red-500">4 Active Requests</Badge>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div>
          <Button variant="outline" size="sm">
            Channel Settings
          </Button>
        </div>
      </div>
      
      {/* Main content - split into chat and sidebar */}
      <div className="flex gap-4 flex-1 overflow-hidden">
        {/* Messages Container */}
        <div className="flex-1 overflow-y-auto px-4 py-4 bg-background/80 rounded-lg">
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-yellow-500 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-medium text-sm">Help Needed Channel</h3>
              <p className="text-xs text-muted-foreground">This channel is for agents to request human assistance on decisions, issues, and situations that require human judgment. High priority requests will be highlighted.</p>
            </div>
          </div>
          
          {conversation.map(msg => (
            <ChatMessage key={msg.id} message={msg} />
          ))}
          <div ref={messagesEndRef} />
        </div>
        
        {/* Sidebar */}
        <div className="w-64 flex-shrink-0 hidden lg:block space-y-4">
          <div className="border rounded-md p-3 bg-background">
            <h3 className="font-medium mb-3 flex items-center">
              <AlertCircle className="h-4 w-4 mr-2 text-red-500" />
              Active Help Requests
            </h3>
            <div>
              {helpRequests.map(request => (
                <HelpRequestCard key={request.id} request={request} />
              ))}
            </div>
          </div>
        </div>
      </div>
      
      {/* Message Input */}
      <div className="mt-4">
        <form onSubmit={handleSendMessage} className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="rounded-full"
          >
            <Paperclip className="h-4 w-4" />
          </Button>
          
          <Input
            value={message}
            onChange={e => setMessage(e.target.value)}
            placeholder="Message #help-needed..."
            className="flex-1"
          />
          
          <Button
            type="submit"
            disabled={!message.trim()}
          >
            <Send className="h-4 w-4 mr-2" />
            Send
          </Button>
        </form>
      </div>
    </div>
  );
};

export default HelpNeededChannelPage;